import React from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {Checkbox, FormControlLabel, TextField} from "@material-ui/core";
import CircleCheckedFilled from "@material-ui/icons/CheckCircle";
import CircleUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import ControlledSelect from "./controlledSelect";
import MultiControlledSelect from "./multiControlledMetadataSelect";
import JSONTextArea from "./jsonTextArea";
import {camelCaseToTitle} from "../utils/camelCaseToTitle";
import {formatInputDate} from "../utils/formatInputDate";
import {validateFeeRanges} from "../utils/validateFeeRanges";

export default function FormDialog({
                                       open,
                                       handleClose,
                                       data,
                                       onChange,
                                       onStateChanged,
                                       onChecked,
                                       handleFormSubmit,
                                       constantsData,
                                       statesList,
                                       metadataCollections,
                                   }) {
    const onDateChange = (e) => {
        const {id, value} = e.target;
        const formattedValue = formatInputDate(value);
        onChange({target: {id, value: formattedValue}});
    };

    return (
        <div>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                PaperProps={{style: {width: "600px"}}}
            >
                <DialogTitle id="alert-dialog-title">Create Fee</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleFormSubmit}>
                        {metadataCollections.map((c) =>
                            c.isMultipleSelection ? (
                                <MultiControlledSelect
                                    id={`metadata.${c.name}`}
                                    label={`Select ${camelCaseToTitle(c.name)}`}
                                    value={data.metadata?.value}
                                    options={constantsData[c.name] || []}
                                    onChange={onChange}
                                    hasError={data.metadata.hasError}
                                    isRequired={Boolean(c?.required)}
                                />
                            ) : (
                                <ControlledSelect
                                    id={`metadata.${c.name}`}
                                    label={`Select ${camelCaseToTitle(c.name)}`}
                                    value={data.metadata?.value}
                                    options={
                                        constantsData[c.name]?.map(
                                            (option) => ({
                                                label: option?.name,
                                                value: option,
                                            }),
                                        ) || []
                                    }
                                    onChange={onChange}
                                    hasError={data.metadata.hasError}
                                    includeAllFlag="false"
                                    isRequired={Boolean(c?.required)}
                                />
                            ),
                        )}
                        <ControlledSelect
                            id="feeState"
                            label="Select State*"
                            value=""
                            options={statesList.map((option) => ({
                                label: `${option?.stateProvinceName} - ${option?.stateProvinceCode}`,
                                value: option,
                            }))}
                            onChange={onStateChanged}
                            hasError={data.feeState.hasError}
                            includeAllFlag="true"
                        />
                        <ControlledSelect
                            id={`feeRangeType`}
                            label={`Select Fee Range Type*`}
                            value={data.feeRangeType.value}
                            options={
                                constantsData.feeRangeTypes?.map(
                                    (option) => ({
                                        label: option?.name || "None",
                                        value: option?.name,
                                    }),
                                ) || []
                            }
                            onChange={onChange}
                            hasError={data.feeRangeType.hasError}
                            includeAllFlag="false"
                        />
                        <>
                            <br/>
                            <JSONTextArea
                                id="feeRanges"
                                label="Select Fee Config Ranges*"
                                placeholder={`[{"range":"0-100","value":100}]`}
                                value={data.feeRanges.value}
                                onChange={onChange}
                                hasError={
                                    data.feeRanges.hasError
                                }
                                validate={validateFeeRanges}
                            />
                        </>
                        <TextField
                            type="text"
                            id="feeAmount"
                            value={data.feeAmount.value}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^[0-9]+\.?[0-9]*$/.test(value)) {
                                    onChange(e);
                                }
                            }}
                            onKeyDown={(event) =>
                                (event.key === "-" ||
                                    event.key === "Minus") &&
                                event.preventDefault()
                            }
                            placeholder="Enter Amount*"
                            label="Amount"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            disabled={data.feeTaxRate.value !== ""}
                            required={data.feeTaxRate.value === ""}
                            inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]+[.]?[0-9]*'
                            }}
                        />
                        <TextField
                            type="number"
                            id="feeTaxRate"
                            value={data.feeTaxRate.value}
                            onChange={(e) => onChange(e)}
                            onKeyDown={(event) =>
                                (event.key === "-" ||
                                    event.key === "Minus") &&
                                event.preventDefault()
                            }
                            placeholder="Enter Rate*"
                            label="Rate"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            disabled={data.feeAmount.value !== ""}
                            required={data.feeAmount.value === ""}
                            inputProps={{min: 1, max: 99}}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(e) => onChecked(e)}
                                    id="feeTaxable"
                                    value={data.feeTaxable.value}
                                    checked={data.feeTaxable.value}
                                    icon={<CircleUnchecked/>}
                                    checkedIcon={<CircleCheckedFilled/>}
                                />
                            }
                            label="Taxable"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(e) => onChecked(e)}
                                    id="feeActive"
                                    value={data.feeActive.value}
                                    checked={data.feeActive.value}
                                    icon={<CircleUnchecked/>}
                                    checkedIcon={<CircleCheckedFilled/>}
                                />
                            }
                            label="Active"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={(e) => onChecked(e)}
                                    id="selfReg"
                                    value={data.selfReg.value}
                                    checked={data.selfReg.value}
                                    icon={<CircleUnchecked/>}
                                    checkedIcon={<CircleCheckedFilled/>}
                                />
                            }
                            label="Self Reg"
                        />
                        <TextField
                            id="description"
                            value={data.description.value}
                            onChange={(e) => onChange(e)}
                            placeholder="Enter Description"
                            label="Description"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                        />
                        <TextField
                            id="startDate"
                            label="Start Date (MM/DD/YYYY)"
                            type="text"
                            value={data.startDate.value}
                            onChange={(e) => onDateChange(e)}
                            placeholder="Enter Start Date"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                        />
                        <TextField
                            id="endDate"
                            label="End Date (MM/DD/YYYY)"
                            type="text"
                            value={data.endDate.value}
                            onChange={(e) => onDateChange(e)}
                            placeholder="Enter End Date"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                        />
                        <ControlledSelect
                            id={`rule`}
                            label={`Select Fee Rule`}
                            value={data.rule.value}
                            options={
                                [
                                    {
                                        label: "None",
                                        value: null,
                                    },
                                    ...(
                                        constantsData.rules?.map(
                                            (option) => ({
                                                label: option?.name || "None",
                                                value: option?.name,
                                            }),
                                        ) || []
                                    )
                                ]
                            }
                            onChange={onChange}
                            hasError={data.rule.hasError}
                            includeAllFlag="false"
                        />
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
